
import { BankType, Sale } from "@/models/base";
import { HttpError } from "@/utils/services/http-error";
import Vue from "vue";
import AppMissingModule from "@/components/common/MissingModule.vue";
import { AuthService } from "@/services/auth-service";
import { MODULE } from "@/config/modules";
import AppFileUpload from "@/components/FileUpload.vue";
import AppErrorMessageBox from "@/components/common/ErrorMessageBox.vue";
import AppPeppolRegistrationStatus from "@/components/peppol/PeppolContactRegistrationStatus.vue";
import { DossierConfigService, SaleService} from "@/services";
import { IParticipantInfoResponse, PeppolService } from "@/services/peppol-service";
import { Tier } from "@/utils/payments/tiers";

export default Vue.extend({
	data() {
		return {
			po: "",
			deliveryNumber: "",
			sending: false,
			files: [] as File[],
			defaultBankType: null as null | BankType,
			settings: DossierConfigService.getPluginConfig().UBL,
			sendCopyToBookkeeper: false,
			registration: null as null | IParticipantInfoResponse
		};
	},
	props: {
		sale: {
			type: Object as() => Sale,
			required: true
		}
	},
	computed: {
		valid():boolean {
			if (!this.registration) return false;
			if (!this.registration.Registered) return false;
			if (this.sale.TypeID == 1 && this.registration.IsInvoiceCompatible) return true;
			if (this.sale.TypeID == 2 && this.registration.IsCreditNoteCompatible) return true;
			if (this.sale.TypeID == 14 && this.registration.IsSelfBilledInvoiceCompatible) return true;
			if (this.sale.TypeID == 15 && this.registration.IsSelfBilledCreditNoteCompatible) return true;
			return false;
		},
		isSupportSession():boolean {
			return AuthService.isDevModeEnabled;
		},
		moduleId():number{
			return MODULE.ELECTRONISCHE_FACTURATIE;
		},
		hasModule():boolean{
			let mod = AuthService.wfCompany.Modules.find(m=>m.ID == this.moduleId);
			if (!mod) {
				return false;
			}
			if (!mod.MetaData) return false;
			let sentsCount = mod.MetaData["max-peppol-sents-per-month"];
			if (!sentsCount) return false;
			if (sentsCount == "0") return false;
			return true;
		},
		maxFileSize():number {
			let baseSize = 50e6;
			for (let file of this.files) {
				baseSize -= file.size;
			}
			return baseSize;
		},
		isDossierFilledIn():boolean{
			return AuthService.wfDossier.isFilledIn();
		},
		isDefaultBankFilledIn():boolean {
			return this.settings.isPeppolConfigured();
		},
		bookkeeperEmail():string {
			return this.settings.BookkeeperEmailConfigs?.sales?.Email || "";
		}
	},
	methods: {
		close() {
			this.$emit("close", false);
		},
		async send() {
			if (this.sale.LastSentViaPeppolDate) {
				let date = this.sale.LastSentViaPeppolDate.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
				let confirm = await this.$confirmBuilder().setWarningTitle().setMessage(this.$ct("confirm-resend-via-peppol", {date})).show();
				if (!confirm) return;
			}
			this.sending = true;
			try{
				await PeppolService.sendSale(this.sale.ID, this.po, this.deliveryNumber, this.files);
				this.$wf.notify(this.$ct("peppol-document-sent"), "success", 3000, "check");
				if(this.sendCopyToBookkeeper){
					await SaleService.batchSendBookkeeper([this.sale]);
				}
				this.close();
			}catch(err){
				let hErr = err as HttpError;
				hErr.dontShow = true;
				this.$wf.notify(hErr.getText(), "danger", 3000, "exclamation-triangle");
			}
			this.sending = false;
		},
		onFileInput(file:File){
			this.files.push(file);
		},
		removeFile(i:number){
			this.files.splice(i,1);
		},
		editDossierSettings(){
			this.$router.push(`/settings/company/dossier/${AuthService.wfDossier.id}`);
		},
		editBanks(){
			this.$router.push("/settings/ubl?tab=peppol");
		},
		async downloadUbl(){
			this.sending = true;
			try{
				await PeppolService.downloadUbl(this.sale.ID, this.po, this.deliveryNumber, this.files);
			}catch(err){
				let hErr = err as HttpError;
				hErr.dontShow = true;
				this.$wf.notify(hErr.getText(), "danger", 3000, "exclamation-triangle");
			}
			this.sending = false;
		},
	},
	async mounted() {
		this.sendCopyToBookkeeper = (this.settings.PeppolSendCopyToBookkeeper && this.bookkeeperEmail !== "" && AuthService.wfCompany.Tier >= Tier.ENTERPRISE);
		if (this.settings.PeppolPoField && this.sale.ExtraFields) {
			const poValue = (this.sale.ExtraFields as any)[this.settings.PeppolPoField] as string;
			if (poValue) {
				this.po = poValue;
			}
		}
		if (this.settings.PeppolDeliveryField && this.sale.ExtraFields) {
			const deliveryValue = (this.sale.ExtraFields as any)[this.settings.PeppolDeliveryField] as string;
			if (deliveryValue) {
				this.deliveryNumber = deliveryValue;
			}
		}
	},
	components: {
		AppMissingModule,
		AppFileUpload,
		AppErrorMessageBox,
		AppPeppolRegistrationStatus
	},
	i18nEx: {
		componentPrefix: "components.sales"
	}
});
